import { supabase } from '@/integrations/supabase/client';
import { CONFIG } from '@/config/config';

export class GoogleSheetSyncService {
  private static async postToWebhook(payload: any, token: string, retries: number = 3): Promise<void> {
    const url = CONFIG.GOOGLE_SHEETS_WEBHOOK_URL;
    if (!url) {
      console.warn('Google Sheets Webhook URL is not configured');
      return;
    }

    const payloadWithToken = {
      ...payload,
      token,
    };

    console.log(`Initiating sync to Google Sheets: ${payload.action}`, { timestamp: payload.timestamp });

    for (let i = 0; i < retries; i++) {
      try {
        if (i > 0) {
          console.log(`Retry attempt ${i} for ${payload.action}...`);
        }

        await fetch(url, {
          method: 'POST',
          mode: 'no-cors', // Common for Google Apps Script webhooks
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payloadWithToken),
        });
        
        console.log(`Sync successful (or assumed so) for ${payload.action}`);
        return;
      } catch (error) {
        console.error(`Attempt ${i + 1} to sync to Google Sheets failed for ${payload.action}:`, error);
        if (i === retries - 1) {
          console.error(`Final attempt failed for ${payload.action}. Sync aborted.`);
        } else {
          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
        }
      }
    }
  }

  static syncSession(sessionId: string): void {
    console.log(`Non-blocking sync initiated for session: ${sessionId}`);
    // Fire and forget
    this._performSyncSession(sessionId).catch(error => {
      console.error('Background _performSyncSession failed:', error);
    });
  }

  private static async _performSyncSession(sessionId: string): Promise<void> {
    try {
      // Fetch session data with players
      const { data: session, error: sessionError } = await supabase
        .from('sessions')
        .select(`
          *,
          session_players (
            *,
            players (*)
          )
        `)
        .eq('id', sessionId)
        .single();

      if (sessionError || !session) {
        throw new Error(sessionError?.message || 'Session not found');
      }

      // Fetch expenses for this session
      const { data: expenses } = await supabase
        .from('expenses')
        .select(`
          *,
          participants:expense_participants(*)
        `)
        .eq('session_id', sessionId);

      const expenseMap: Record<string, { paid: number, share: number }> = {};
      (expenses || []).forEach(exp => {
        if (!expenseMap[exp.payer_id]) expenseMap[exp.payer_id] = { paid: 0, share: 0 };
        expenseMap[exp.payer_id].paid += Number(exp.amount);
        
        exp.participants.forEach((p: any) => {
          if (!expenseMap[p.player_id]) expenseMap[p.player_id] = { paid: 0, share: 0 };
          expenseMap[p.player_id].share += Number(p.share);
        });
      });

      const payload = {
        action: 'upsert',
        data: {
          id: session.id,
          date: session.date,
          status: session.status,
          created_at: session.created_at,
          players: session.session_players.map((sp: any) => {
            const expData = expenseMap[sp.player_id] || { paid: 0, share: 0 };
            return {
              player_name: sp.players.name,
              player_nickname: sp.players.nickname,
              buy_ins: sp.buy_ins,
              cash_out: sp.cash_out,
              net_amount: sp.net_amount,
              expense_paid: expData.paid,
              expense_share: expData.share,
              total_net: (sp.net_amount || 0) + (expData.paid - expData.share),
            };
          }),
        },
        timestamp: new Date().toISOString(),
      };

      await this.postToWebhook(payload, CONFIG.SYNC_TOKEN);
    } catch (error) {
      console.error('Failed to sync session to Google Sheets:', error);
    }
  }

  static syncDeleteSession(sessionId: string): void {
    console.log(`Non-blocking sync initiated for session deletion: ${sessionId}`);
    const payload = {
      action: 'delete_session',
      data: { id: sessionId },
      timestamp: new Date().toISOString(),
    };
    this.postToWebhook(payload, CONFIG.SYNC_TOKEN).catch(error => {
      console.error('Failed to sync session deletion to Google Sheets:', error);
    });
  }

  static syncWipe(): void {
    console.log('Non-blocking sync initiated for data wipe');
    // Fire and forget
    this._performSyncWipe().catch(error => {
      console.error('Background _performSyncWipe failed:', error);
    });
  }

  private static async _performSyncWipe(): Promise<void> {
    try {
      const payload = {
        action: 'delete',
        timestamp: new Date().toISOString(),
      };

      await this.postToWebhook(payload, CONFIG.SYNC_TOKEN);
    } catch (error) {
      console.error('Failed to sync wipe to Google Sheets:', error);
    }
  }
}
